import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const digits = (v) => v.replace(/[^0-9]/g, "");
const field = "w-full rounded-xl border border-indigo-100 bg-[#F4F6FD] px-4 py-3 text-slate-800 outline-none focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/70";

export default function ChangePinDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const close = (v) => { setOpen(v); if (!v) { setForm({ current: "", next: "", confirm: "" }); setError(""); } };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next.length < 4) return setError("New PIN must be at least 4 digits.");
    if (form.next !== form.confirm) return setError("New PIN and confirmation do not match.");
    setSaving(true);
    try {
      await api.post("/auth/change-pin", { current_pin: form.current, new_pin: form.next });
      toast.success("PIN changed. Use your new PIN next time you log in.");
      close(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const input = (key, label, testId) => (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-blue-900/80">{label}</span>
      <input data-testid={testId} type="password" inputMode="numeric" autoComplete={key === "current" ? "current-password" : "new-password"}
        value={form[key]} onChange={(e) => setForm({ ...form, [key]: digits(e.target.value) })} className={field} required />
    </label>
  );

  return (
    <>
      <button data-testid="change-pin-button" onClick={() => setOpen(true)} aria-label="Change PIN"
        className="grid h-10 w-10 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:text-blue-600">
        <KeyRound className="h-5 w-5" />
      </button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent data-testid="change-pin-dialog" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-blue-900">Change PIN</DialogTitle>
            <DialogDescription>If you never changed it, your current PIN is 1234.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {input("current", "Current PIN", "change-pin-current")}
            {input("next", "New PIN", "change-pin-new")}
            {input("confirm", "Confirm New PIN", "change-pin-confirm")}
            {error && <p data-testid="change-pin-error" className="text-sm font-medium text-red-600">{error}</p>}
            <button data-testid="change-pin-submit" type="submit" disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-700 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-700/25 hover:bg-blue-800 disabled:opacity-70">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save New PIN"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
